import React, { useEffect } from 'react';
import styles from 'styles/components/question-builders/QuestionBuildersList.module.css';
import { ExpandableQuestion } from 'components/expandable/ExpandableQuestion';
import QuestionSchema from '@ax2/common/model/schemas/QuestionSchema';
import { getQuestionPreview } from 'helpers/questionPreviewBuilder';
import { getQuestion } from 'helpers/questionBuilder';
import QuestionType from '@ax2/common/model/QuestionType';
import { createQuestionSchema } from 'helpers/questionSchemaCreator';
import QuestionRecyclingBin from '../question-recycling-bin/QuestionRecyclingBin';
import {
  transfer,
  fixOrder,
  addUnique,
  removeByValue,
  findNext,
} from '@ax2/common/helpers/array';
import _ from 'lodash';
import { ActionButton } from '@ax2/common/components/ActionButton';
import { QuestionListHeader } from 'components/QuestionListHeader';
import { getQuestionsValidation } from 'helpers/questionBuilderValidations';
import UUID from '@ax2/common/model/UUID';
import {
  useSurveyContext,
  useSurveyDispatchContext,
} from '../../contexts/SurveyContext';
import { AddCustomQuestion } from 'components/question-builders/AddCustomQuestion';
import { createQuestion, storeSurvey } from 'rest/makeSurveyClient';
import { getSurveyErrors } from 'helpers/survey';
import nextIcon from '../../assets/arrow_right_white.svg';
import { Element as ScrollElement, scroller } from 'react-scroll';
import { Events } from 'react-scroll/modules';
import { isEditable } from 'helpers/question';
import config from '../../config';
import { getErrorMessage } from '@ax2/common/helpers/error';
import { RestError } from '@ax2/common/rest/baseClient';
import { useErrorUpdateContext } from '@ax2/common/contexts/ErrorContext';
import SurveySchema from '@ax2/common/model/schemas/SurveySchema';
import ConfirmationModal, {
  ConfirmationModalData,
} from 'components/ConfirmationModal';
import { usePdfDownload } from 'helpers/hooks/usePdfDownload';
import { saveIcon } from 'rest/shopClient';

export interface QuestionBuildersListData {
  onFinish: () => void;
  colorFunction: () => void;
  setcolor3false: () => void;
}

const topActionsId = '_qbl-top-errors';

export const QuestionBuildersList: React.FC<
  QuestionBuildersListData
> = props => {
  const surveySchema = useSurveyContext();
  const surveyDispatch = useSurveyDispatchContext();
  const updateError = useErrorUpdateContext();
  const { loading, pdfDownload } = usePdfDownload(surveySchema);
  const [openEndedWarning, setOpenEndedWarning] = React.useState<
    ConfirmationModalData
  >();
  const [isOpenEndedWarningShown, setOpenEndedWarningShown] = React.useState<
    boolean
  >(false);

  useEffect(() => {
    if (surveySchema && !surveySchema.title && config.type === 'demo') {
      surveyDispatch({ type: 'title', title: 'Demo Survey' }); //Prefill title in fast Demo mode
    }
  }, [surveySchema, surveyDispatch]);

  // 'New' questions are those which were just created, replaced, or restored from a trash bin
  const [newQuestions, setNewQuestions] = React.useState<UUID[]>([]);
  const [expandedQuestions, setExpandedQuestions] = React.useState<UUID[]>([]);
  const [isErrorsVisible, setIsErrorsVisible] = React.useState<boolean>(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const questionsError = surveySchema
    ? getQuestionsValidation(surveySchema.questions)
    : [];
  const surveyErrors = getSurveyErrors(surveySchema);
  const isSurveyValid = surveyErrors.length === 0;
  const visibleSurveyError =
    isErrorsVisible && !isSurveyValid ? surveyErrors[0] : null;

  useEffect(() => {
    // `isSaving` is used here only so that multiple button pressing will always
    // scroll to error even if nothing has changed
    if (!isSaving && visibleSurveyError) {
      scroller.scrollTo(topActionsId, {
        duration: 400,
        delay: 0,
        offset: -10,
        smooth: 'easeInOutQuart',
      });
    }
  }, [isSaving, visibleSurveyError]);

  const updateSurveyWithQuestions = (
    questions: QuestionSchema[],
    deleted?: QuestionSchema[]
  ) => {
    if (surveySchema) {
      surveyDispatch({
        type: 'questions',
        questions: fixOrder(questions),
        deleted,
      });
    }
  };
  const updateStoreWithQuestions = async (
    questions: QuestionSchema[],
    deleted?: QuestionSchema[]
  ) => {
    updateSurveyWithQuestions(questions, deleted);
    if (surveySchema) {
      await storeSurvey(
        deleted
          ? {
              ...surveySchema,
              questions: fixOrder(questions),
              deleted: deleted,
            }
          : {
              ...surveySchema,
              questions: fixOrder(questions),
            },
        isSurveyValid
      );
    }
  };

  const getNewQuestions = (newQuestion: QuestionSchema, position?: number) => {
    if (surveySchema) {
      const questions = [...surveySchema.questions];
      if (position !== undefined) {
        questions[position] = newQuestion;
      } else {
        questions.push(newQuestion);
      }
      return questions;
    } else {
      return null;
    }
  };
  const changeQuestion = async (
    newQuestion: QuestionSchema,
    position?: number
  ) => {
    try {
      const newQuestions = getNewQuestions(newQuestion, position);
      if (newQuestions) {
        updateSurveyWithQuestions(newQuestions);
      }
    } catch (e) {
      updateError({
        text: getErrorMessage(e as RestError),
      });
    }
  };
  const addQuestion = async (
    newQuestion: QuestionSchema,
    position?: number
  ) => {
    try {
      const newQuestions = getNewQuestions(newQuestion, position);
      if (newQuestions) {
        await updateStoreWithQuestions(newQuestions);
      }
    } catch (e) {
      updateError({
        text: getErrorMessage(e as RestError),
      });
    }
  };
  const addNewEmptyQuestion = async () => {
    try {
      if (surveySchema) {
        const questionWithMaxOrder = _.maxBy(surveySchema.questions, 'order');
        const order = questionWithMaxOrder ? questionWithMaxOrder.order + 1 : 0;
        const id = await createQuestion(surveySchema.id);
        const newQuestion = createQuestionSchema(id, order, QuestionType.Empty);
        if (newQuestion) {
          // Order is important: call before `addQuestion`
          setNewQuestions(qs => qs.concat(id));
          props.setcolor3false();
          saveIcon(`color2+${surveySchema.id}`, 'false');
          saveIcon(`color3+${surveySchema.id}`, 'false');
          await addQuestion(newQuestion);
        }
      }
    } catch (e) {
      updateError({
        text: getErrorMessage(e as RestError),
      });
    }
  };
  const removeQuestion = async (id: UUID) => {
    try {
      if (surveySchema) {
        const [questions, deleted] = transfer(
          surveySchema.questions,
          surveySchema.deleted,
          question => question.id === id
        );
        props.setcolor3false();
        setExpandedQuestions(removeByValue(expandedQuestions, id));
        await updateStoreWithQuestions(questions, deleted);
      }
    } catch (e) {
      updateError({
        text: getErrorMessage(e as RestError),
      });
    }
  };
  const recoveryQuestion = async (id: UUID) => {
    try {
      if (surveySchema && surveySchema.questions.length < 10) {
        const [deleted, questions] = transfer(
          surveySchema.deleted,
          surveySchema.questions,
          question => question.id === id
        );
        // Order is important: call before `updateStoreWithQuestions`
        setNewQuestions(qs => qs.concat(id));
        await updateStoreWithQuestions(questions, deleted);
      }
    } catch (e) {
      updateError({
        text: getErrorMessage(e as RestError),
      });
    }
  };
  const saveAsDraftHandler = async () => {
    try {
      setIsErrorsVisible(true);
      if (surveySchema) {
        setIsSaving(true);
        await storeSurvey(surveySchema, isSurveyValid);
        setIsSaving(false);
      }
    } catch (e) {
      setIsSaving(false);
      updateError({
        text: getErrorMessage(e as RestError),
      });
    }
  };

  const hasOpenEndedQuestions = (survey: SurveySchema): boolean => {
    const openEnded = survey.questions.filter(
      (q: QuestionSchema) =>
        q.questionType === QuestionType.OpenEndedText ||
        q.questionType === QuestionType.OpenEndedNumeric
    );
    return openEnded && openEnded.length > 0;
  };

  const save = async () => {
    try {
      setIsErrorsVisible(true);

      if (surveySchema) {
        if (!isOpenEndedWarningShown && hasOpenEndedQuestions(surveySchema)) {
          setOpenEndedWarningShown(true);
          setOpenEndedWarning({
            text:
              'Please be sure your open-ended question does not ask for any Personally Identifiable Information (PII) such as name, address, phone number, date of birth, demographic information, or any personal identifiers (ex. SSN, AMA#, NPI#, etc.).',
            onClose: () => setOpenEndedWarning(undefined),
            actions: [
              {
                text: 'OK',
                onClick: () => setOpenEndedWarning(undefined),
              },
            ],
          });
        } else if (isSurveyValid) {
          props.colorFunction();
          props.onFinish();
          // Survey will be stored at this callback;
        } else {
          setIsSaving(true);
          await storeSurvey(surveySchema, false);
          setIsSaving(false);
        }
      }
    } catch (e) {
      setIsSaving(false);
      updateError({
        text: getErrorMessage(e as RestError),
      });
    }
  };

  return (
    <>
      {getActions(
        topActionsId,
        visibleSurveyError,
        saveAsDraftHandler,
        save,
        isSaving,
        pdfDownload,
        loading
      )}
      <QuestionListHeader
        title="Step 2"
        subtitle="Panel Survey Questions (10 max)"
        notes={['You are only allowed up to 3 open response questions']}
        helperText="If you change your mind you can restore your recently deleted questions from the recycling bin at the bottom of the page."
      />
      {openEndedWarning && <ConfirmationModal {...openEndedWarning} />}
      {surveySchema &&
        _.map(surveySchema.questions, (question, index) => (
          <div className={styles.questionWrapper} key={question.id}>
            <ScrollElement name={`scrollQuestion_${question.id}`}>
              <ExpandableQuestion
                id={'qbl-question-' + (index + 1)}
                title={'Question ' + (index + 1)}
                subtitle={isEditable(question) ? 'Editable' : 'Non-Editable'}
                state={
                  isErrorsVisible
                    ? questionsError[index]
                      ? 'needsEdit'
                      : 'edited'
                    : undefined
                }
                isNew={newQuestions.includes(question.id)}
                onNewProcessed={() =>
                  setNewQuestions(qs => qs.filter(id => id !== question.id))
                }
                removeCompletion={async () => await removeQuestion(question.id)}
                replaceCompletion={async () => {
                  const newQuestion = createQuestionSchema(
                    question.id,
                    question.order,
                    QuestionType.Empty
                  );
                  if (newQuestion) {
                    await addQuestion(newQuestion, index);
                  }
                }}
                swapCompletion={updateStoreWithQuestions}
                previewContent={getQuestionPreview(question)}
                expandedContent={getQuestion(
                  question,
                  false,
                  async (q: QuestionSchema) => await changeQuestion(q, index),
                  {
                    onTypeChange: async (t: QuestionType) => {
                      const newQuestion = createQuestionSchema(
                        question.id,
                        question.order,
                        t
                      );
                      if (newQuestion) {
                        await addQuestion(newQuestion, index);
                      }
                    },
                    saveCompletion: async () => {
                      try {
                        if (
                          surveySchema &&
                          _.findIndex(
                            surveySchema.questions,
                            q => q.questionType !== QuestionType.Empty
                          ) !== -1
                        ) {
                          await storeSurvey(surveySchema, isSurveyValid);
                          const nextQuestion = findNext(
                            surveySchema.questions,
                            q => q.id === question.id
                          );

                          if (nextQuestion) {
                            const updatedExpandedQuestions = removeByValue(
                              expandedQuestions,
                              question.id
                            );
                            setTimeout(() => {
                              new Promise(resolve => {
                                Events.scrollEvent.register('end', () => {
                                  resolve();
                                  Events.scrollEvent.remove('end');
                                });
                                scroller.scrollTo(
                                  'scrollQuestion_' + nextQuestion.id,
                                  {
                                    duration: 400,
                                    delay: 0,
                                    offset: -20,
                                    smooth: 'easeInOutQuart',
                                  }
                                );
                              }).then(() => {
                                setExpandedQuestions(
                                  addUnique(
                                    updatedExpandedQuestions,
                                    nextQuestion.id
                                  )
                                );
                              });
                            }, 1200); // (hide content) + (show preview) = time to 'collapse' current question

                            setExpandedQuestions(updatedExpandedQuestions);
                          } else {
                            setExpandedQuestions(
                              removeByValue(expandedQuestions, question.id)
                            );
                          }
                        }
                      } catch (e) {
                        updateError({
                          text: getErrorMessage(e as RestError),
                        });
                      }
                    },
                  }
                )}
                isExpanded={expandedQuestions.includes(question.id)}
                onExpand={state => {
                  setExpandedQuestions(
                    state
                      ? addUnique(expandedQuestions, question.id)
                      : removeByValue(expandedQuestions, question.id)
                  );
                }}
              />
            </ScrollElement>
          </div>
        ))}
      {surveySchema &&
        (surveySchema.questions.length < 10 && (
          <AddCustomQuestion
            id={'qbl-add-custom-q-' + (surveySchema.questions.length + 1)}
            onAddQuestion={addNewEmptyQuestion}
          />
        ))}
      {surveySchema && !!surveySchema.deleted.length && (
        <div className={styles.recyclingBinWrapper}>
          <QuestionRecyclingBin
            questions={surveySchema.deleted}
            onPutBack={recoveryQuestion}
          />
        </div>
      )}
      {getActions(
        undefined,
        null,
        saveAsDraftHandler,
        save,
        isSaving,
        pdfDownload,
        loading
      )}
    </>
  );
};

const getActions = (
  id: string | undefined,
  error: string | null,
  onSaveAsDraft: () => void,
  onSaveAndSend: () => void,
  isSaving: boolean,
  onDownload?: () => void,
  loading?: boolean | undefined
) => (
  <div className={styles.actionsContent} id={id}>
    <div className={styles.actionsWithError}>
      <ActionButton
        text="Save as draft"
        type="secondaryAlt"
        onClick={async () => !isSaving && onSaveAsDraft()}
      />
      &nbsp; &nbsp;
      {onDownload ? (
        <ActionButton
          text="Download"
          type="primary"
          onClick={() => onDownload && onDownload()}
          disabled={loading !== undefined ? loading : false}
          loading={loading !== undefined ? loading : false}
        />
      ) : null}
      {error && (
        <div className={styles.error}>
          <i className="material-icons">error_outline</i>
          <span>{error}</span>
        </div>
      )}
    </div>
    <ActionButton
      text="Save and continue to preview & send"
      type="primaryAlt"
      icon={{
        source: nextIcon,
        position: 'right',
      }}
      onClick={async () => !isSaving && onSaveAndSend()}
    />
  </div>
);
